import { Action } from 'redux';
import { ClipboardInfo, Rectangle, ClipbookInfo } from 'ark-common';
import { Vector2D } from '../../types/common';
import { ComposerMode, ClipbookState, PagePicture } from '../types';
import { DrawingState } from '../../drawing/reducers/drawing';
import { PictureStorageState } from '../../drawing/reducers/pictureStorage';

export enum ActionType {
  SET_CLIENT_RECT = 'COMPOSER_SET_CLIENT_RECT',
  SET_COMPOSER_MODE = 'COMPOSER_SET_COMPOSER_MODE',
  ZOOM_IN = 'COMPOSER_ZOOM_IN',
  ZOOM_OUT = 'COMPOSER_ZOOM_OUT',
  RESET_ZOOM = 'COMPOSER_RESET_ZOOM',
  SET_PAN = 'COMPOSER_SET_PAN',
  RECEIVE_NEW_PAGE_FROM_CLIPBOARD = 'COMPOSER_RECEIVE_NEW_PAGE_FROM_CLIPBOARD',
  SET_CROPPING_RECT = 'COMPOSER_SET_CROPPING_RECT',
  DISCARD_CLIPBOARD = 'COMPOSER_DISCARD_CLIPBOARD',
  CREATE_NEW_CLIPBOOK = 'COMPOSER_CREATE_NEW_CLIPBOOK',
  ADD_NEW_PAGE = 'COMPOSER_ADD_NEW_PAGE',
  ADD_NEW_PAGE_WITH_PICTURE = 'COMPOSER_ADD_NEW_PAGE_WITH_PICTURE',
  MOVE_TO_PAGE = 'COMPOSER_MOVE_TO_PAGE',
  LOAD_CLIPBOOK_INDEX = 'COMPOSER_LOAD_CLIPBOOK_INDEX',
  START_LOADING_CLIPBOOK = 'COMPOSER_START_LOADING_CLIPBOOK',
  ABORT_LOADING_CLIPBOOK = 'COMPOSER_ABORT_LOADING_CLIPBOOK',
  FINISH_LOADING_CLIPBOOK = 'COMPOSER_FINISH_LOADING_CLIPBOOK',
  FINISH_SAVING_CLIPBOOK = 'COMPOSER_FINISH_SAVING_CLIPBOOK',
}

interface SetClientRectAction extends Action {
  type: ActionType.SET_CLIENT_RECT;
  clientRect: Rectangle;
}

interface SetComposerModeAction extends Action {
  type: ActionType.SET_COMPOSER_MODE;
  mode: ComposerMode;
}

interface ZoomInAction extends Action {
  type: ActionType.ZOOM_IN;
}

interface ZoomOutAction extends Action {
  type: ActionType.ZOOM_OUT;
}

interface ResetZoomAction extends Action {
  type: ActionType.RESET_ZOOM;
}

interface SetPanAction extends Action {
  type: ActionType.SET_PAN;
  pan: Vector2D;
}

interface ReceiveNewPageFromClipboardAction extends Action {
  type: ActionType.RECEIVE_NEW_PAGE_FROM_CLIPBOARD;
  pictureID: string;
  pictureBlob: Blob;
  pictureURL: string;
  pictureInfo: ClipboardInfo;
}

interface SetCroppingRectAction extends Action {
  type: ActionType.SET_CROPPING_RECT;
  rect: Rectangle;
}

interface DiscardClipboardAction extends Action {
  type: ActionType.DISCARD_CLIPBOARD;
}

interface CreateNewClipbookAction extends Action {
  type: ActionType.CREATE_NEW_CLIPBOOK;
  id: string;
}

interface AddNewPageAction extends Action {
  type: ActionType.ADD_NEW_PAGE;
  uniqueID: string;
  backgroundID: string;
  width: number;
  height: number;
}

interface AddNewPageWithPictureAction extends Action {
  type: ActionType.ADD_NEW_PAGE_WITH_PICTURE;
  uniqueID: string;
  backgroundID: string;
  backgroundBlob: Blob;
  backgroundURL: string;
  width: number;
  height: number;
}

interface MoveToPageAction extends Action {
  type: ActionType.MOVE_TO_PAGE;
  destination: number;
}

interface LoadClipbookIndexAction extends Action {
  type: ActionType.LOAD_CLIPBOOK_INDEX;
  index: ReadonlyArray<ClipbookInfo>;
}

interface StartLoadingClipbookAction extends Action {
  type: ActionType.START_LOADING_CLIPBOOK;
}

interface AbortLoadingClipbookAction extends Action {
  type: ActionType.ABORT_LOADING_CLIPBOOK;
}

interface FinishLoadingClipbookAction extends Action {
  type: ActionType.FINISH_LOADING_CLIPBOOK;
  clipbook: ClipbookState;
  pagePictures: PictureStorageState<PagePicture>;
  drawings: { readonly [pageID: string]: DrawingState };
  drawingPictures: PictureStorageState<HTMLCanvasElement>;
}

interface FinishSavingClipbookAction extends Action {
  type: ActionType.FINISH_SAVING_CLIPBOOK;
}

export const setClientRect = (clientRect: Rectangle): SetClientRectAction => ({
  clientRect, type: ActionType.SET_CLIENT_RECT,
});

export const setComposerMode = (mode: ComposerMode): SetComposerModeAction => ({
  mode, type: ActionType.SET_COMPOSER_MODE,
});

export const zoomIn = (): ZoomInAction => ({ type: ActionType.ZOOM_IN });
export const zoomOut = (): ZoomOutAction => ({ type: ActionType.ZOOM_OUT });
export const resetZoom = (): ResetZoomAction => ({ type: ActionType.RESET_ZOOM });
export const setPan = (pan: Vector2D): SetPanAction => ({ pan, type: ActionType.SET_PAN });

export const receiveNewPageFromClipboard = (
  pictureID: string, pictureBlob: Blob, pictureURL: string, pictureInfo: ClipboardInfo,
): ReceiveNewPageFromClipboardAction => ({
  pictureID, pictureBlob, pictureURL, pictureInfo,
  type: ActionType.RECEIVE_NEW_PAGE_FROM_CLIPBOARD,
});

export const setCroppingRect = (rect: Rectangle): SetCroppingRectAction => ({
  rect, type: ActionType.SET_CROPPING_RECT,
});

export const discardClipboard = (): DiscardClipboardAction => ({
  type: ActionType.DISCARD_CLIPBOARD,
});

export const createNewClipbook = (id: string): CreateNewClipbookAction => ({
  id, type: ActionType.CREATE_NEW_CLIPBOOK,
});

export const addNewPage = (
  uniqueID: string, backgroundID: string, width: number, height: number,
): AddNewPageAction => ({
  uniqueID, backgroundID, width, height, type: ActionType.ADD_NEW_PAGE,
});

export const addNewPageWithPicture = (
  uniqueID: string,
  backgroundID: string,
  backgroundBlob: Blob,
  backgroundURL: string,
  width: number,
  height: number,
): AddNewPageWithPictureAction => ({
  uniqueID, backgroundID, backgroundBlob, backgroundURL, width, height,
  type: ActionType.ADD_NEW_PAGE_WITH_PICTURE,
});

export const moveToPage = (destination: number): MoveToPageAction => ({
  destination, type: ActionType.MOVE_TO_PAGE,
});

export const loadClipbookIndex = (index: ReadonlyArray<ClipbookInfo>): LoadClipbookIndexAction => ({
  index, type: ActionType.LOAD_CLIPBOOK_INDEX,
});

export const startLoadingClipbook = (): StartLoadingClipbookAction => ({
  type: ActionType.START_LOADING_CLIPBOOK,
});

export const abortLoadingClipbook = (): AbortLoadingClipbookAction => ({
  type: ActionType.ABORT_LOADING_CLIPBOOK,
});

export const finishLoadingClipbook = (
  clipbook: ClipbookState,
  pagePictures: PictureStorageState<PagePicture>,
  drawings: { readonly [pageID: string]: DrawingState },
  drawingPictures: PictureStorageState<HTMLCanvasElement>,
): FinishLoadingClipbookAction => ({
  clipbook, pagePictures, drawings, drawingPictures,
  type: ActionType.FINISH_LOADING_CLIPBOOK,
});

export const finishSavingClipbook = (): FinishSavingClipbookAction => ({
  type: ActionType.FINISH_SAVING_CLIPBOOK,
});

type ComposerAction =
  SetClientRectAction | SetComposerModeAction | ZoomInAction | ZoomOutAction | ResetZoomAction |
  SetPanAction | ReceiveNewPageFromClipboardAction | SetCroppingRectAction |
  DiscardClipboardAction | CreateNewClipbookAction | AddNewPageAction |
  AddNewPageWithPictureAction | MoveToPageAction |
  LoadClipbookIndexAction | StartLoadingClipbookAction | AbortLoadingClipbookAction |
  FinishLoadingClipbookAction | FinishSavingClipbookAction;

export function isComposerAction(action: Action): action is ComposerAction {
  return Object.keys(ActionType).some(key => action.type === ActionType[key as any]);
}

export default ComposerAction;
